import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { FormikDropdownField, FormikTextField } from '@components/FormikFields';

import type { GetAccountSettingQuery } from 'generated/license.generated';

import type { LicensePlanValues } from '../types';

const messages = defineMessages({
  title: {
    id: 'licenseCard.title',
    defaultMessage: 'Organization License: {type}',
  },
  patientSeats: {
    id: 'newAccount.patientSeats',
    defaultMessage: 'Patient seats',
  },
  practitionerSeats: {
    id: 'newAccount.practitionerSeats',
    defaultMessage: 'Practitioner seats are unlimited.',
  },
  licenseCustom: {
    id: 'newAccount.licenseCustom',
    defaultMessage: 'Custom',
  },
  seatsBelowAllocated: {
    id: 'orgSettings.licensePlan.seatsBelowAllocated',
    defaultMessage:
      'Patient plans already allocate {allocated} seats. Raise patient seats to at least {allocated} to save.',
  },
});

/**
 * The seat field holds a patient count, so it is sized to its content rather
 * than to the wizard's side-by-side field width.
 */
const SEATS_FIELD_WIDTH = 140;

interface LicenseCardProps {
  license: GetAccountSettingQuery['getAccountSetting'];
  /**
   * Seats already reserved across the org's plans — the floor the seat field
   * may not drop below without over-committing the pool.
   */
  allocated: number;
}

/**
 * The licence and patient seat fields. Presentational: the values live in the
 * page's form and are persisted by its single Save.
 */
export default function LicenseCard({ license, allocated }: LicenseCardProps) {
  const { formatMessage } = useIntl();
  const { isSubmitting, values } = useFormikContext<LicensePlanValues>();

  // Only the org's own licence is offered, matching the creation wizard. Re-
  // pointing an account at a template licence would discard its feature set;
  // shared tiers rejoin this list in a later iteration.
  const licenseOptions = [
    {
      k: license?.license_id ?? '',
      v: formatMessage(messages.licenseCustom),
    },
  ];

  const enteredSeats = Number(values.limit_patients);
  // Read from values rather than `errors.limit_patients`: FormikTextField only
  // surfaces an error once touched, and the warning should appear as soon as
  // the value is entered.
  const seatsBelowAllocated =
    values.limit_patients !== '' &&
    Number.isFinite(enteredSeats) &&
    enteredSeats < allocated;

  return (
    <Card>
      <CardHeader
        title={
          <Typography
            variant="h6"
            sx={{
              display: 'flex',
              columnGap: 0.5,
            }}
          >
            {formatMessage(messages.title, {
              type: (
                <Typography
                  color="primary.main"
                  data-testid="license-card-name"
                  variant="h6"
                >
                  {license?.license?.name ?? '-'}
                </Typography>
              ),
            })}
          </Typography>
        }
      />
      <CardContent>
        <Stack gap={2}>
          <Box>
            <FormikDropdownField
              label=""
              name="license_id"
              options={licenseOptions}
              required
            />
          </Box>

          <Stack alignItems="center" direction="row" gap={2}>
            <Box width={SEATS_FIELD_WIDTH}>
              <FormikTextField
                disabled={isSubmitting}
                fullWidth
                label={formatMessage(messages.patientSeats)}
                name="limit_patients"
                placeholder="0"
                required
                type="number"
              />
            </Box>
            <Typography color="text.secondary" variant="body2">
              {formatMessage(messages.practitionerSeats)}
            </Typography>
          </Stack>

          {seatsBelowAllocated && (
            <Box>
              <Typography
                color="error"
                data-testid="patient-seats-below-allocated"
                variant="body2"
              >
                {formatMessage(messages.seatsBelowAllocated, { allocated })}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
